/* eslint-disable react/display-name */
import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Table } from '@amzn/awsui-components-react';
import { ChannelRollUp, YTDRollUp } from '@amzn/merp-core/models';

import { TableRow } from 'modules/core/model';
import { RollUpsHeader } from '../RollUpsHeader';
import { ExpandableCell } from 'modules/core/components/Table';
import { AbbreviatedMonths } from 'modules/rollups/constants';
import { CsvConverter } from 'modules/rollups/csv';
import { CSVTemplate } from 'modules/dashboard/models';
import { CSVLink } from 'react-csv';

type ChannelProps = {
    items: ChannelRollUp[];
    loading?: boolean;
};

export const Channel = (props: ChannelProps) => {
    const { items, loading } = props;

    const { t } = useTranslation('rollups');

    const [rows, updateRows] = useState<TableRow<ChannelRollUp>[]>([]);
    const [exportLoading, updateExportLoading] = useState(false);

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const csvlink = useRef<any>();
    const [csvExport, setCsvExport] = useState<CSVTemplate | undefined>();

    const onRowExpanded = (rowNumber: number) => {
        const newRows = [...rows];
        const { expanded } = newRows[rowNumber];
        newRows[rowNumber].expanded = !expanded;

        updateRows(newRows);
    };

    const onExport = () => {
        updateExportLoading(true);

        const result = new CsvConverter().channel(rows.map(r => r.payload));
        setCsvExport(result);
    };

    useEffect(() => {
        if (!csvExport) return;

        // The csv library needs some time to load the csv data and make the blob.
        // this effect runs before the ref is rendered, so we ned to wait for the
        // ref to be rendered before triggering the click
        setTimeout(() => {
            csvlink.current?.link.click();
            updateExportLoading(false);
            setCsvExport(undefined);
        }, 500);
    }, [csvExport]);

    useEffect(() => {
        updateRows(prev => {
            if (prev.length === 0) {
                return items.map((payload, number) => ({
                    number,
                    payload,
                    expanded: false,
                    selected: false,
                    rowId: payload.channel
                }));
            } else {
                return items.map((payload, i) => Object.assign(prev[i], { payload, rowId: payload.channel }));
            }
        });
    }, [items]);

    function getCountryCountString(e: TableRow<ChannelRollUp>) {
        if (Object.keys(e.payload.countries).length === 0) {
            return '';
        } else if (Object.keys(e.payload.countries).length === 1) {
            return ' (1 country)';
        } else {
            return `(${Object.keys(e.payload.countries).length} countries)`;
        }
    }

    const columns = [
        {
            id: 'channel',
            header: <div>{t('channel')}</div>,
            cell: (e: TableRow<ChannelRollUp>) => (
                <ExpandableCell
                    row={e}
                    cell={
                        <span className="mt-auto mb-auto">
                            {`${t('channelMap.' + e.payload.channel)} ${getCountryCountString(e)}`}
                        </span>
                    }
                    control={Object.keys(e.payload.countries).length > 0}
                    content={Object.keys(e.payload.countries).map((c: string) => (
                        <span key={c}>{c}</span>
                    ))}
                    onChange={onRowExpanded}
                />
            )
        },
        {
            id: 'YTM',
            header: <div>{t('YTM')}</div>,
            cell: (e: TableRow<ChannelRollUp>) => (
                <ExpandableCell
                    row={e}
                    cell={<>{e.payload.ytd}</>}
                    content={Object.values(e.payload.countries).map((c: YTDRollUp, i) => (
                        <span key={`${c.year}-${i}`}></span>
                    ))}
                />
            )
        },
        ...AbbreviatedMonths.map(month => {
            const header = month.charAt(0).toUpperCase() + month.slice(-month.length + 1);

            return {
                id: month,
                header,
                cell: (e: TableRow<ChannelRollUp>) => (
                    <ExpandableCell
                        row={e}
                        cell={<>{e.payload[month]}</>}
                        content={Object.entries(e.payload.countries).map(([key, c]: [string, YTDRollUp]) => (
                            <span key={`${month}-${key}`}>{c[month]}</span>
                        ))}
                    />
                )
            };
        })
    ];

    let header = t('channel');

    if (rows.length > 0) {
        header += ` (${rows.length})`;
    }

    return (
        <div className="msp-expandable-table">
            {csvExport && (
                <CSVLink
                    ref={csvlink}
                    data={csvExport.getCSVData()}
                    headers={csvExport.getHeaders()}
                    filename={'msp_channel_rollups.csv'}
                    className="hidden"
                    target="_blank"
                />
            )}
            <Table
                columnDefinitions={columns}
                items={rows}
                loading={loading}
                loadingText={t('loading')}
                header={
                    <RollUpsHeader
                        disabled={rows.length <= 0}
                        header={header}
                        exportLoading={exportLoading}
                        onExport={onExport}
                    />
                }
            />
        </div>
    );
};
